import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { getModalConfig } from '../LocationsContainer/getModalConfig';
import { UserData } from '../LocationsContainer/types';
import {
  createDebounceEffect,
  transformUsersForModal
} from '../LocationsContainer/utils';
import styles from '../styles.module.css';
import UnlinkIcon from '@/assets/icons/unlink.svg?react';
import UserIcon from '@/assets/icons/user.svg?react';
import {
  AdminCardHeader,
  AdminCardBody,
  AdminTableBody
} from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import DataTable, { DataTableColumn } from '@/components/DataTable';
import { Button } from '@/components/Form/Button';
import Loader from '@/components/Form/Loader';
import PlusButton from '@/components/Form/PlusButton';
import columnStyles from '@/components/LocationDetails/columnsStyles.module.css';
import SearchComponent from '@/components/LocationDetails/searchComponent';
import { adminToolPaths } from '@/constants/adminTool';
import useModal from '@/context/ModalContext/useModal';
import { useGetUsersInitial, useSearchUsers } from '@/hooks/services/Users';

const usersColumns = (
  showActions: boolean = true,
  handleUnassignClick: (user: UserData) => void,
  handleUserClick?: (username: string) => void,
  alignActions: 'left' | 'center' | 'right' = 'left'
): DataTableColumn<UserData>[] => {
  const baseColumns: DataTableColumn<UserData>[] = [
    {
      field: 'name',
      headerName: 'NAME',
      width: showActions ? '30%' : '33%',
      sortable: true,
      onClick: user => {
        handleUserClick?.(user?.name || '');
      }
    },
    {
      field: 'email',
      headerName: 'EMAIL',
      width: showActions ? '30%' : '33%',
      sortable: true
    },
    {
      field: 'userRole',
      headerName: 'USER ROLE',
      width: showActions ? '24%' : '34%',
      sortable: true
    },
    {
      headerName: 'ACTIONS',
      width: '16%',
      renderCell: ({ row }) => (
        <div
          className={
            columnStyles[
              `header${alignActions.charAt(0).toUpperCase() + alignActions.slice(1)}`
            ]
          }
        >
          <Button
            label="UNASSIGN"
            icon={UnlinkIcon}
            iconPosition="right"
            onClick={() => handleUnassignClick?.(row)}
            color="blue"
            solid={false}
            size="small"
            className={styles.unassignButton}
          />
        </div>
      )
    }
  ];

  if (showActions) {
    return baseColumns;
  } else {
    return baseColumns.filter(column => column.headerName !== 'ACTIONS');
  }
};

export type OnAssignUsers = (
  users: Array<{ name: string; username: string; id?: string }>,
  options?: { onSuccess?: () => void }
) => Promise<void>;

export type OnUnassignUser = (username: string) => Promise<void>;

interface UsersAssignedTableProps {
  usersForTable: UserData[];
  canAssignUnassignUsers: boolean;
  onAssignUsers: OnAssignUsers;
  onUnassignUser: OnUnassignUser;
  loading?: boolean;
}

export const UsersAssignedTable = ({
  usersForTable,
  canAssignUnassignUsers = false,
  onAssignUsers,
  onUnassignUser,
  loading
}: UsersAssignedTableProps) => {
  const navigate = useNavigate();
  const { updateModalContent, closeModal } = useModal();
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [usersSearchTerm, setUsersSearchTerm] = useState('');
  const [debouncedUsersSearchTerm, setDebouncedUsersSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<
    Array<{ id: string; name: string; username: string; [key: string]: any }>
  >([]);

  const {
    data: dataUsersInitial,
    isLoading: isLoadingUsersInitial,
    error: errorUsersInitial,
    isError: isErrorUsersInitial
  } = useGetUsersInitial({
    useQueryOptions: {
      enabled: isUserModalOpen,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  });

  const {
    data: dataUsers,
    isLoading: isLoadingUsers,
    error: errorUsers,
    isError: isErrorUsers
  } = useSearchUsers(debouncedUsersSearchTerm, {
    useQueryOptions: {
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: isUserModalOpen && debouncedUsersSearchTerm.length > 0
    }
  });

  useEffect(
    createDebounceEffect(usersSearchTerm, setDebouncedUsersSearchTerm),
    [usersSearchTerm]
  );

  const usersForModal = useMemo(() => {
    const usersToUse =
      debouncedUsersSearchTerm.length > 0
        ? dataUsers?.data || []
        : dataUsersInitial?.data || [];
    const isError =
      debouncedUsersSearchTerm.length > 0 ? isErrorUsers : isErrorUsersInitial;
    return transformUsersForModal(usersToUse, isError);
  }, [
    dataUsers,
    isErrorUsers,
    dataUsersInitial,
    isErrorUsersInitial,
    debouncedUsersSearchTerm
  ]);

  const handleCloseUserModal = useCallback(() => {
    closeModal();
    setIsUserModalOpen(false);
    setUsersSearchTerm('');
    setDebouncedUsersSearchTerm('');
    setSelectedUsers([]);
  }, [closeModal]);

  const handleUsersSearchChange = useCallback((value: string) => {
    setUsersSearchTerm(value);
  }, []);

  const handleOpenAssignModal = useCallback(() => {
    setUsersSearchTerm('');
    setDebouncedUsersSearchTerm('');
    setSelectedUsers([]);
    setIsUserModalOpen(true);
  }, []);

  const updateUserModalContent = useCallback(() => {
    if (!isUserModalOpen) return;

    const config = getModalConfig(
      'user',
      [],
      usersForModal,
      [],
      handleCloseUserModal,
      [],
      undefined,
      [],
      undefined,
      selectedUsers,
      async (users: Array<{ name: string; username: string; id?: string }>) =>
        onAssignUsers(users, { onSuccess: handleCloseUserModal })
    );

    const createUserModalContent = () => {
      if (isErrorUsers || isErrorUsersInitial) {
        return (
          <div className={styles.errorContainer}>
            Error loading users data:{' '}
            {(errorUsers || errorUsersInitial)?.message || 'Unknown error'}
          </div>
        );
      }

      const showLoader =
        debouncedUsersSearchTerm.length > 0
          ? isLoadingUsers
          : isLoadingUsersInitial;

      return (
        <div className={styles.formModal}>
          <SearchComponent
            icon={config.icon || null}
            title={config.title || ''}
            onSearchChange={handleUsersSearchChange}
            placeholder="Search by email"
          />
          <div className={styles.formBody}>
            {showLoader ? (
              <div className={styles.loadingContainer}>
                <Loader size="large" />
              </div>
            ) : (
              <DataTable
                rows={(config.rows as UserData[]) || []}
                className={styles.dataTableModal}
                columns={config.columns || []}
                selectable={true}
                selectedRows={selectedUsers as any}
                onRowSelectionChange={setSelectedUsers as any}
                getRowId={row => row.id}
                initialState={{
                  sorting: {
                    field: 'name',
                    sort: 'asc'
                  }
                }}
              />
            )}
          </div>
        </div>
      );
    };

    updateModalContent(createUserModalContent(), {
      modalButtons: config.modalButtons
    });
  }, [
    isUserModalOpen,
    isErrorUsers,
    isErrorUsersInitial,
    errorUsers,
    errorUsersInitial,
    usersForModal,
    debouncedUsersSearchTerm.length,
    isLoadingUsers,
    isLoadingUsersInitial,
    selectedUsers,
    handleCloseUserModal,
    handleUsersSearchChange,
    onAssignUsers,
    updateModalContent
  ]);

  useEffect(() => {
    if (isUserModalOpen) {
      updateUserModalContent();
    }
  }, [isUserModalOpen, updateUserModalContent]);

  const handleUnassignClick = useCallback(
    (user: UserData) => {
      updateModalContent(null, {
        modalTitle: 'Unassign User',
        modalIcon: UserIcon,
        className: styles.unassignModalContainer,
        modalButtons: [
          {
            label: 'CANCEL',
            onClick: () => {
              closeModal();
            },
            variant: 'cancelBlue'
          },
          {
            label: 'UNASSIGN USER',
            onClick: async () => {
              try {
                await onUnassignUser(user.id || user.name);
              } catch (error) {
                console.error('Error in unassign user:', error);
              }
            },
            variant: 'okBlue',
            isLoader: true
          }
        ],
        alignButtons: 'center',
        contentTitle: 'You are about to unassign',
        contentSubtitle: `User ${user.name}`,
        contentDescription: (
          <div className={styles.contentDescription}>
            <span>Are you sure you want to proceed?</span>
          </div>
        )
      });
    },
    [updateModalContent, closeModal, onUnassignUser]
  );

  const handleUserClick = (username: string) => {
    navigate(adminToolPaths.users.detail.replace(':userId', username));
  };

  return (
    <div className={styles.adminContainer}>
      <AdminCardHeader
        left={
          <>
            <UserIcon className={styles.adminIconHeader} />
            <h2 className={styles.adminTitle}>Users</h2>
          </>
        }
        right={
          canAssignUnassignUsers ? (
            <PlusButton onClick={handleOpenAssignModal} label="Assign a user" />
          ) : undefined
        }
      />

      {usersForTable.length > 0 ? (
        <AdminTableBody>
          <DataTable
            rows={usersForTable}
            className={styles.dataTable}
            columns={usersColumns(
              canAssignUnassignUsers,
              handleUnassignClick,
              handleUserClick
            )}
            initialState={{
              sorting: {
                field: 'name',
                sort: 'asc'
              }
            }}
          />
        </AdminTableBody>
      ) : (
        <AdminCardBody>
          <div className={styles.adminContainerCenteredRow}>
            {loading ? (
              <ContainerLoader />
            ) : (
              <h4 className={styles.adminText}>
                No users have been assigned yet
              </h4>
            )}
          </div>
        </AdminCardBody>
      )}
    </div>
  );
};
